import useLanguage from "../i18n/useLanguage";
import React, { useState, useEffect } from "react";
import { FaWhatsapp, FaCheck } from "react-icons/fa";
import { catalogProducts as defaultCatalog } from "../data/catalog";
import { fetchCatalog } from "../api/client";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function CatalogPage() {
  const { t } = useLanguage();
  const [catalogList, setCatalogList] = useState(defaultCatalog);
  const [activeCategory, setActiveCategory] = useState("Semua");
  const categories = [
    "Semua",
    "Design",
    "Development",
    "Game Dev",
    "Immersive Tech",
  ];

  useEffect(() => {
    let isMounted = true;
    fetchCatalog(defaultCatalog).then((data) => {
      if (isMounted && data && data.length > 0) {
        setCatalogList(data);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredProducts =
    activeCategory === "Semua"
      ? catalogList
      : catalogList.filter((p) => p.category === activeCategory);

  const STUDIO_PHONE = "62881011844173";

  return (
    <div className="font-sans bg-black text-white min-h-screen flex flex-col overflow-x-hidden">
      <Navbar />

      <main className="flex-grow pt-32 pb-24 px-6 md:px-10 relative overflow-hidden">
        {/* Background radial glow */}
        <div
          className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none opacity-20"
          style={{
            background:
              "radial-gradient(circle, rgba(252, 211, 77, 0.25) 0%, rgba(0, 0, 0, 0) 70%)",
          }}
        />

        <div className="w-full max-w-7xl mx-auto relative z-10">
          {/* Page Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-gray-400 text-sm md:text-base tracking-[0.3em] uppercase font-semibold mb-3">{t("Katalog Layanan Digital")}{" "}</p>
            <h1 className="text-4xl md:text-6xl font-bold text-amber-300 mb-6">{t("Catalog Penjualan")}{" "}</h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">{t("Jelajahi paket solusi digital dari Titik Senyap Studio. Pilih paket yang sesuai, dan tombol beli akan langsung menghubungkan Anda dengan tim kami di WhatsApp beserta template pesan resmi.")}{" "}</p>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-14">
            {categories.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${
                    isActive
                      ? "bg-amber-300 text-black border-amber-300 shadow-[0_0_15px_rgba(252,211,77,0.3)]"
                      : "bg-white/5 text-gray-300 border-white/10 hover:border-amber-300/40 hover:text-white"
                  }`}
                >
                  {t(cat)}
                </button>
              );
            })}
          </div>

          {/* Catalog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {filteredProducts.map((product) => {
              const waUrl = `https://wa.me/${STUDIO_PHONE}?text=${encodeURIComponent(
                t(product.waMessage)
              )}`;

              return (
                <div
                  key={product.id}
                  className="group relative flex flex-col justify-between rounded-2xl border border-white/10 bg-neutral-950/90 p-8 hover:border-amber-300/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.6)]"
                >
                  <div>
                    {/* Badge & Category */}
                    <div className="flex items-center justify-between gap-4 mb-5">
                      <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">
                        {t(product.category)}
                      </span>
                      <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-300/15 text-amber-300 border border-amber-300/30">
                        {t(product.badge)}
                      </span>
                    </div>

                    {/* Title & Tagline */}
                    <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-300 transition-colors">
                      {t(product.title)}
                    </h2>
                    <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                      {t(product.tagline)}
                    </p>

                    {/* Price */}
                    <div className="py-4 border-y border-white/10 mb-6">
                      <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1">{t("Investasi Layanan")}{" "}</span>
                      <div className="text-2xl md:text-3xl font-bold text-amber-300">
                        {t(product.price)}
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="mb-8">
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">{t("Termasuk dalam paket:")}{" "}</p>
                      <ul className="space-y-3">
                        {product.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 text-sm text-gray-300"
                          >
                            <FaCheck className="text-amber-300 text-xs mt-1 shrink-0" />
                            <span className="leading-snug">{t(feature)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* WhatsApp Action Button */}
                  <div className="pt-2 mt-auto">
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-xl bg-amber-300 text-black font-bold text-base hover:bg-amber-400 active:scale-[0.98] transition-all duration-200 shadow-[0_4px_16px_rgba(252,211,77,0.25)]"
                      aria-label={`${t("Beli via WhatsApp")}: ${t(product.title)}`}
                    >
                      <FaWhatsapp className="text-xl" />
                      <span>{t("Beli via WhatsApp")}</span>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
